// src/modules/walkInCase/pages/WalkInCaseDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getWalkInCaseById, generateBill, generateAgreement, deleteWalkInCase, restoreWalkInCase, startWalkInESign, finalizeWalkInESign } from '../api';
import { useDeleteConfirm } from '../../../components/DeleteConfirm';
import { addCaseToBranchVault } from '../../goldVault/api';
import GoldVaultLabel from '../../../components/GoldVaultLabel';
import useESignWatcher from '../../../hooks/useESignWatcher';
import {
  Card, Typography, Spin, Tag, Button, Row, Col,
  Table, Divider, Steps, message, Popconfirm, Space, Tabs, Statistic, Modal, Alert,
} from 'antd';
import {
  FilePdfOutlined,
  DollarCircleOutlined, EditOutlined, LockOutlined,
  FileTextOutlined, CheckCircleOutlined, GoldOutlined,
  SafetyOutlined, InfoCircleOutlined, CopyOutlined, LinkOutlined,
  DollarOutlined, SafetyCertificateOutlined,
  UserOutlined, BankOutlined, TeamOutlined, ProfileOutlined,
} from '@ant-design/icons';
import { SummarySection, SummaryLayout, InfoGrid } from '../../../components/CaseSummary';
import { calculateCaseRoundOff, formatRoundOff } from '../../../utils/goldCalculation';
import dayjs from 'dayjs';
import { usePermissions } from '../../../hooks/usePermissions';
import SettlementPaymentTable from '../../pledgeReleaseCase/components/SettlementPaymentTable';
import VoucherTable from '../../pledgeReleaseCase/components/VoucherTable';
import 'bootstrap/dist/css/bootstrap.min.css';

const { Title, Text } = Typography;

const STATUS_COLORS = {
  NEW: 'default', EVALUATED: 'blue', PAYMENT_SETTLED: 'purple', BILL_GENERATED: 'cyan',
  IN_INVENTORY: 'gold', DISPATCHED: 'orange', SETTLED: 'green', CLOSED: 'lime',
};

// Pipeline order: the customer is PAID first and the bill is raised LAST, so the
// bill can carry the full split-payment trail.
const STEPS = [
  { key: 'NEW', title: 'New' },
  { key: 'EVALUATED', title: 'Evaluated' },
  { key: 'PAYMENT_SETTLED', title: 'Settlement' },
  { key: 'CLOSED', title: 'Bill' },
];

// How far along the stepper each status sits. Legacy (bill-first) cases carry
// BILL_GENERATED and the downstream inventory statuses — they are all past the
// settlement stage, so they render on the final step.
const STATUS_STEP_INDEX = {
  NEW: 0,
  EVALUATED: 1,
  PAYMENT_SETTLED: 2,
  BILL_GENERATED: 3,
  IN_INVENTORY: 3,
  DISPATCHED: 3,
  SETTLED: 3,
  CLOSED: 3,
};

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v || 0);

// Item prices, gold value and processing fee are exact — shown to 4 decimals.
const fmt4 = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(v || 0);

export default function WalkInCaseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wic, setWic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [activeTab, setActiveTab] = useState('summary');
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();
  const { canEditModule, can, canViewField } = usePermissions();

  // eSign state
  const [eSignLoading, setESignLoading] = useState(false);
  const [eSignModal, setESignModal] = useState({ visible: false, signUrl: '', requestId: '', documentId: '' });

  // Vault state
  const [vaultLoading, setVaultLoading] = useState(false);
  const [vaultLabelVisible, setVaultLabelVisible] = useState(false);

  // Voucher refresh trigger
  const [voucherRefreshTrigger, setVoucherRefreshTrigger] = useState(0);

  // Field-level view permission checks
  const canViewCustomer = canViewField('walkInCase', 'customerId');
  const canViewGoldItems = canViewField('walkInCase', 'goldItems');
  const canViewProcessingFee = canViewField('walkInCase', 'processingFee');
  const canViewRemarks = canViewField('walkInCase', 'remarks');
  const canViewTotalPurchase = canViewField('walkInCase', 'totalPurchaseAmount');
  const canViewStatus = canViewField('walkInCase', 'status');

  // silent: refresh in place (eSign signals) without the full-page spinner.
  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await getWalkInCaseById(id);
      setWic(res.data);
    } catch {
      if (!silent) messageApi.error('Failed to load case');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Leegality's webhook / the signer's return settles the case on the server and
  // pushes `esign.updated`; we just re-read the case. No "Signing Complete" click.
  const eSignWatcher = useESignWatcher({
    documentId: eSignModal.documentId,
    active: eSignModal.visible,
    onSignal: () => load({ silent: true }),
  });

  useEffect(() => {
    if (!eSignModal.visible || !wic?.esign || wic.esign.documentId !== eSignModal.documentId) return;
    if (wic.esign.status === 'completed') {
      eSignWatcher.closePopup();
      setESignModal({ visible: false, signUrl: '', requestId: '', documentId: '' });
      messageApi.success('Agreement signed — signed copy saved.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wic?.esign?.status, wic?.esign?.documentId, eSignModal.visible, eSignModal.documentId]);

  const doAction = async (label, fn) => {
    setActionLoading(label);
    try {
      await fn();
      messageApi.success(`${label} successful`);
      await load();
    } catch (e) {
      messageApi.error(e.response?.data?.message || `${label} failed`);
    } finally {
      setActionLoading('');
    }
  };

  const { confirmDelete, confirmPermanentDelete, confirmRestore, contextHolder: deleteModalHolder, isAdmin } = useDeleteConfirm();

  const handleDelete = async (permanent) => {
    try {
      await deleteWalkInCase(id, { permanent });
      messageApi.success(permanent ? 'Case permanently deleted' : 'Case deleted');
      setTimeout(() => navigate('/walk-in-cases'), 500);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to delete case');
    }
  };

  const handleRestore = async () => {
    try {
      await restoreWalkInCase(id);
      messageApi.success('Case restored');
      await load();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to restore case');
    }
  };

  // ── eSign Handlers ──
  const handleESignStart = async () => {
    setESignLoading(true);
    try {
      const res = await startWalkInESign(id);
      const { signUrl, requestId, documentId } = res.data || {};
      if (signUrl) {
        setESignModal({ visible: true, signUrl, requestId: requestId || '', documentId: documentId || '' });
        messageApi.success('eSign request created!');
        eSignWatcher.open(signUrl); // may be popup-blocked after the await; the modal has a button
      } else {
        messageApi.error('eSign provider did not return a signing link. Please try again.');
      }
      await load();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to start eSign');
    } finally {
      setESignLoading(false);
    }
  };

  const handleESignFinalize = async () => {
    setESignLoading(true);
    try {
      await finalizeWalkInESign(id);
      messageApi.success('eSign finalized successfully!');
      eSignWatcher.closePopup();
      setESignModal({ visible: false, signUrl: '', requestId: '', documentId: '' });
      await load({ silent: true });
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to finalize eSign');
    } finally {
      setESignLoading(false);
    }
  };

  const handleESignCancel = () => {
    eSignWatcher.closePopup();
    setESignModal({ visible: false, signUrl: '', requestId: '', documentId: '' });
  };

  const handleMoveToVault = async () => {
    const branchId = wic.branch?._id || wic.branch;
    if (!branchId) {
      messageApi.error('Branch not found on case — cannot move to vault');
      return;
    }
    setVaultLoading(true);
    try {
      await addCaseToBranchVault(branchId, { caseId: wic._id, caseModel: 'WalkInPurchaseCase' });
      messageApi.success('Gold moved to branch vault');
      await load();
      setVaultLabelVisible(true);
    } catch (e) {
      messageApi.error(e?.response?.data?.message || 'Failed to move to vault');
    } finally {
      setVaultLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!wic) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Text type="secondary">Case not found.</Text>
      </div>
    );
  }

  const c = wic;
  const customer = c.customerId || {};
  // A signing session only counts as live if the provider actually handed back
  // a signUrl. A 'started' row without one is a failed attempt, not a pending
  // signature — without this the UI dead-ends with no retry path.
  const eSignInProgress = c.esign?.status === 'started' && Boolean(c.esign?.signUrl);
  const hasSignedCopy = c.esign?.status === 'completed' && Boolean(c.esign?.signedDocumentUrl);
  // Editing has to stop as soon as money starts moving — a payout is measured
  // against the agreed amount, so re-pricing afterwards would move the target.
  const isCaseLocked = ['PAYMENT_SETTLED', 'BILL_GENERATED', 'IN_INVENTORY', 'DISPATCHED', 'SETTLED', 'CLOSED']
    .includes(c.status) || (c.totalSettlementPaid || 0) > 0;
  const roundingUpAmt = c.roundingUpAmount || 0;
  // Automatic nearest-rupee round-off folded into the final payment.
  const roundOffAmt = calculateCaseRoundOff(c.totalPurchaseAmount, c.processingFeeAmount, roundingUpAmt, c.finalPaymentToCustomer);

  // Settlement / vault gate
  const settlementAmt = c.finalPaymentToCustomer || 0;
  const totalSettlementPaid = c.totalSettlementPaid || 0;
  // Derived, not read from remainingSettlementBalance: that field is a cache and
  // cases priced before payouts moved ahead of billing still carry its default of 0.
  const remainingSettlement = parseFloat((settlementAmt - totalSettlementPaid).toFixed(2));
  const isFullyPaid = settlementAmt > 0 && remainingSettlement <= 0.5;
  const notYetVaulted = !['IN_BRANCH_VAULT', 'IN_TRANSFER', 'IN_HO_VAULT', 'BATCHED'].includes(c.inventoryStatus);
  // The gold is only shelved once the Bill exists — billing is the last step of
  // the case, and the vault entry has to sit behind a completed purchase.
  const isBilled = !!c.billId
    || ['BILL_GENERATED', 'IN_INVENTORY', 'DISPATCHED', 'SETTLED', 'CLOSED'].includes(c.status);
  const canMoveToVault = notYetVaulted && isFullyPaid && isBilled;

  // Settlement payouts open once the case is priced; the bill is the final step
  // and only unlocks after the customer has been paid in full.
  const settlementUnlocked = !['NEW'].includes(c.status) && settlementAmt > 0;
  const canGenerateBill = !c.billId && c.status === 'PAYMENT_SETTLED';

  const goldColumns = [
    { title: '#', render: (_, __, idx) => idx + 1, width: 50 },
    { title: 'Item Name', dataIndex: 'itemName' },
    { title: 'Purity (%)', dataIndex: 'purity', render: (v) => `${v}%` },
    { title: 'Gross Wt. (gm)', dataIndex: 'grossWeight', render: (v) => v?.toFixed(4) },
    { title: 'Stone/Wax Ded. (gm)', dataIndex: 'stoneWaxDeduction', render: (v) => v?.toFixed(4) },
    { title: 'Dust Ded. (gm)', dataIndex: 'dustDeduction', render: (v) => v?.toFixed(4) },
    { title: 'Net Wt. (gm)', dataIndex: 'netWeight', render: (v) => v?.toFixed(4) },
    { title: 'Rate/gm', dataIndex: 'ratePerGram', render: (v) => fmt(v) },
    { title: 'Total Price', dataIndex: 'totalPrice', render: (v) => <strong>{fmt4(v)}</strong> },
  ];

  /* ════════════════════════════════════════
     TAB CONTENT DEFINITIONS
     ════════════════════════════════════════ */
  const tabItems = [
    /* ── 1. Summary ── */
    {
      key: 'summary',
      label: <span><InfoCircleOutlined /> Summary</span>,
      children: (
        <div>
          <SummaryLayout>
            {canViewCustomer && (
              <SummarySection title="Customer" icon={<UserOutlined />}>
                <InfoGrid
                  fields={[
                    { label: 'Name', value: customer.name },
                    { label: 'Phone', value: customer.phone, mono: true },
                    { label: 'Aadhaar', value: customer.aadhaar, mono: true },
                    { label: 'PAN', value: customer.pan, mono: true },
                    { label: 'Address', value: customer.address, wide: true },
                  ]}
                />
              </SummarySection>
            )}

            <SummarySection title="Case Information" icon={<InfoCircleOutlined />}>
              <InfoGrid
                fields={[
                  { label: 'Created By', value: c.createdBy?.name },
                  { label: 'Created At', value: c.createdAt && dayjs(c.createdAt).format('DD MMM YYYY, hh:mm A') },
                  { label: 'Last Updated', value: c.updatedAt && dayjs(c.updatedAt).format('DD MMM YYYY, hh:mm A') },
                  canViewRemarks && c.remarks && { label: 'Remarks', value: c.remarks, wide: true },
                ]}
              />
            </SummarySection>

            {/* Where a bank payout actually lands — the same details the
                approver is shown when they release the payment. */}
            {canViewCustomer && (
              <SummarySection title="Bank Payout Details" icon={<BankOutlined />}>
                <InfoGrid
                  fields={[
                    { label: 'Bank A/c No.', value: c.bankAccountNumber, mono: true },
                    { label: 'IFSC', value: c.ifscCode, mono: true },
                    { label: 'Bank Branch', value: c.bankBranchName, wide: true },
                  ]}
                />
              </SummarySection>
            )}

            {c.agentId && (
              <SummarySection title="Agent" icon={<TeamOutlined />}>
                <InfoGrid
                  fields={[
                    { label: 'Agent', value: `${c.agentId.name || '-'}${c.agentId.agentId ? ` (${c.agentId.agentId})` : ''}`, wide: true },
                    { label: 'Commission %', value: `${c.agentCommissionPercent ?? 0}%` },
                    { label: 'Commission Amount', value: fmt(c.agentCommissionAmount) },
                  ]}
                />
              </SummarySection>
            )}

            {canViewRemarks && (
              <SummarySection title="Additional Details" icon={<ProfileOutlined />} full>
                <InfoGrid
                  columns={3}
                  fields={[
                    { label: 'Reason for Selling Gold', value: c.reasonForSelling },
                    { label: 'Gold Ownership', value: c.goldOwnership },
                    { label: 'Alternate Phone No.', value: c.alternatePhone, mono: true },
                    { label: 'Relative / Reference Person', value: c.referenceDetails },
                    { label: 'Reference Phone No.', value: c.referencePhone, mono: true },
                  ]}
                />
              </SummarySection>
            )}
          </SummaryLayout>

          {/* Gold Items Table */}
          {canViewGoldItems && c.goldItems && c.goldItems.length > 0 && (
            <>
              <Divider orientation="left">Gold Items</Divider>
              <Table
                dataSource={c.goldItems.map((item, idx) => ({ ...item, key: idx }))}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 'max-content' }}
                columns={goldColumns}
                summary={() => (
                  <>
                    {canViewTotalPurchase && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={3} align="right"><strong>Totals</strong></Table.Summary.Cell>
                        <Table.Summary.Cell><strong>{c.totalGrossWeight?.toFixed(4)} gm</strong></Table.Summary.Cell>
                        <Table.Summary.Cell><strong>{c.totalStoneDeduction?.toFixed(4)} gm</strong></Table.Summary.Cell>
                        <Table.Summary.Cell><strong>{c.totalDustDeduction?.toFixed(4)} gm</strong></Table.Summary.Cell>
                        <Table.Summary.Cell><strong>{c.totalNetWeight?.toFixed(4)} gm</strong></Table.Summary.Cell>
                        <Table.Summary.Cell />
                        <Table.Summary.Cell><strong>{fmt4(c.totalPurchaseAmount)}</strong></Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    {canViewProcessingFee && (c.processingFee > 0 || c.processingFeeAmount > 0) && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={8} align="right">
                          <Text type="secondary">Processing Fee ({c.processingFee || 0}%)</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <Text type="secondary">- {fmt4(c.processingFeeAmount)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    {roundingUpAmt !== 0 && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={8} align="right">
                          <Text type="secondary">Rounding Up</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <Text type="secondary" style={{ color: roundingUpAmt > 0 ? '#52c41a' : '#cf1322' }}>
                            {roundingUpAmt > 0 ? '+ ' : '- '}{fmt(Math.abs(roundingUpAmt))}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    {canViewTotalPurchase && roundOffAmt !== 0 && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={8} align="right">
                          <Text type="secondary">Round Off (to nearest ₹)</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>{formatRoundOff(roundOffAmt)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    {canViewTotalPurchase && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={8} align="right">
                          <Text strong style={{ fontSize: 16 }}>Final Payment to Customer</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <Text strong style={{ fontSize: 16, color: '#3f8600' }}>
                            {fmt(c.finalPaymentToCustomer)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  </>
                )}
              />
            </>
          )}

          {/* Linked Records */}
          {(c.inventoryBatchId || c.smelterDispatchId) && (
            <>
              <Divider orientation="left">Linked Records</Divider>
              <Space wrap>
                {c.inventoryBatchId && (
                  <Button
                    type="default"
                    size="small"
                    icon={<GoldOutlined />}
                    onClick={() => navigate(`/gold-inventory/${c.inventoryBatchId._id || c.inventoryBatchId}`)}
                  >
                    Inventory Batch: {c.inventoryBatchId.batchId || c.inventoryBatchId}
                  </Button>
                )}
                {c.smelterDispatchId && (
                  <Button
                    type="default"
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={() => navigate(`/smelters/dispatches/${c.smelterDispatchId._id || c.smelterDispatchId}`)}
                  >
                    Smelter Dispatch: {c.smelterDispatchId.dispatchId || c.smelterDispatchId}
                  </Button>
                )}
              </Space>
            </>
          )}
        </div>
      ),
    },

    /* ── 2. Agreement ── */
    {
      key: 'agreement',
      label: <span><SafetyOutlined /> Agreement</span>,
      children: (
        <div>
          {c.purchaseAgreementUrl ? (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* eSign Status */}
              <Row align="middle" gutter={12}>
                <Col>
                  <Text strong>Agreement Status:</Text>
                </Col>
                <Col>
                  {c.esign?.status === 'completed' ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>Signed</Tag>
                  ) : eSignInProgress ? (
                    <Tag color="orange">Pending Signature</Tag>
                  ) : c.esign?.status === 'failed' || c.esign?.status === 'started' ? (
                    <Tag color="red">eSign Failed</Tag>
                  ) : (
                    <Tag color="default">Not Signed</Tag>
                  )}
                </Col>
              </Row>

              {/* Signed at the provider, but we never got the file back */}
              {c.esign?.status === 'completed' && (!c.esign?.signedDocumentUrl || !c.esign?.auditTrailUrl) && (
                <Alert
                  type="warning"
                  showIcon
                  message={c.esign?.signedDocumentUrl ? 'Audit trail not downloaded yet' : 'Signed copy not downloaded yet'}
                  description={
                    c.esign?.signedDocumentUrl
                      ? 'The signed agreement is saved, but the Aadhaar audit trail has not been retrieved. Use "Download Audit Trail" to retry.'
                      : 'The document was signed, but the signed PDF has not been saved to the CRM. Use "Download Signed Copy" to retry.'
                  }
                />
              )}

              {/* Why the last attempt failed */}
              {!eSignInProgress && (c.esign?.status === 'failed' || c.esign?.status === 'started') && (
                <Alert
                  type="error"
                  showIcon
                  message="Last eSign attempt failed"
                  description={
                    c.esign?.metadata?.error ||
                    'The eSign provider did not return a signing link. Check the customer profile (gender, pincode, state, date of birth) and try again.'
                  }
                />
              )}

              <Space wrap>
                {/* View PDF — only call it "signed" when we actually hold the
                    signed copy, otherwise this links to the unsigned draft. */}
                <Button
                  type="primary"
                  icon={<FilePdfOutlined />}
                  href={hasSignedCopy ? c.esign.signedDocumentUrl : c.purchaseAgreementUrl}
                  target="_blank"
                >
                  {hasSignedCopy ? 'View Signed Agreement' : 'View Agreement PDF'}
                </Button>

                {/* Aadhaar audit trail — the record of the OTP/ESP transaction */}
                {c.esign?.auditTrailUrl && (
                  <Button
                    icon={<SafetyOutlined />}
                    href={c.esign.auditTrailUrl}
                    target="_blank"
                  >
                    View Audit Trail
                  </Button>
                )}

                {/* Recover the signed copy when finalize marked the case
                    complete but the download failed */}
                {c.esign?.status === 'completed' && (!c.esign?.signedDocumentUrl || !c.esign?.auditTrailUrl) && (
                  <Button
                    icon={<FilePdfOutlined />}
                    loading={eSignLoading}
                    onClick={handleESignFinalize}
                  >
                    {c.esign?.signedDocumentUrl ? 'Download Audit Trail' : 'Download Signed Copy'}
                  </Button>
                )}

                {/* Re-Generate */}
                {!isCaseLocked && c.esign?.status !== 'completed' && !eSignInProgress && (
                  <Button
                    icon={<FilePdfOutlined />}
                    loading={actionLoading === 'Generate Agreement'}
                    onClick={() => doAction('Generate Agreement', () => generateAgreement(id))}
                  >
                    Re-Generate Agreement
                  </Button>
                )}

                {/* eSign Button — start new signing */}
                {c.esign?.status !== 'completed' && !eSignInProgress && (
                  <Button
                    type="primary"
                    ghost
                    icon={<SafetyOutlined />}
                    loading={eSignLoading}
                    onClick={handleESignStart}
                  >
                    {c.esign?.status ? 'Retry eSign' : 'eSign Agreement'}
                  </Button>
                )}

                {/* Open existing signing page if already started */}
                {eSignInProgress && (
                  <Button
                    type="primary"
                    ghost
                    icon={<LinkOutlined />}
                    onClick={() =>
                      setESignModal({
                        visible: true,
                        signUrl: c.esign.signUrl,
                        requestId: c.esign.requestId || '',
                        documentId: c.esign.documentId || '',
                      })
                    }
                  >
                    Open Signing Page
                  </Button>
                )}
              </Space>
            </Space>
          ) : (
            <Space direction="vertical" size="middle">
              <Text type="secondary">No purchase agreement generated yet.</Text>
              {!['NEW'].includes(c.status) && (
                <Button
                  type="primary"
                  icon={<FilePdfOutlined />}
                  loading={actionLoading === 'Generate Agreement'}
                  onClick={() => doAction('Generate Agreement', () => generateAgreement(id))}
                >
                  Generate Agreement
                </Button>
              )}
            </Space>
          )}
        </div>
      ),
    },

    /* ── 3. Settlement — the customer is paid BEFORE the bill is raised ── */
    ...(settlementUnlocked
      ? [
        {
          key: 'settlement',
          label: (
            <span>
              <DollarOutlined /> Settlement
            </span>
          ),
          children: (
            <div>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Pay the customer first, then generate the bill."
                description="Record every payout here. Once the full amount is paid the Bill step unlocks, and the bill is issued carrying all of these payments and their reference numbers."
              />
              <SettlementPaymentTable
                caseId={c._id}
                caseData={c}
                caseModel="WalkInPurchaseCase"
                onPaymentAdded={() => {
                  load();
                  setVoucherRefreshTrigger(prev => prev + 1);
                }}
              />
              <Divider />
              <VoucherTable
                caseId={id}
                caseData={c}
                refreshTrigger={voucherRefreshTrigger}
                onVoucherUpdate={load}
                isWalkInCase={true}
              />
            </div>
          ),
        },
      ]
      : []),

    /* ── 4. Bill — the LAST step ── */
    {
      key: 'bill',
      label: <span><FileTextOutlined /> Bill</span>,
      children: (
        <div>
          {c.billId ? (
            <Row align="middle" gutter={16} className="mb-3">
              <Col>
                <Button
                  type="primary"
                  ghost
                  icon={<FileTextOutlined />}
                  onClick={() => navigate(`/billing/viewCreatedBill/${typeof c.billId === 'object' ? c.billId._id : c.billId}`)}
                >
                  View Bill {c.billId?.billId ? `(${c.billId.billId})` : ''}
                </Button>
              </Col>
              <Col>
                <Tag
                  color={STATUS_COLORS[c.status] || 'default'}
                  style={{ fontSize: 13, padding: '4px 14px' }}
                >
                  {(c.status || '').replace(/_/g, ' ')}
                </Tag>
              </Col>
            </Row>
          ) : canGenerateBill ? (
            <Space direction="vertical" size="middle">
              <Alert
                type="success"
                showIcon
                message="Customer paid in full — ready to bill."
                description={`${fmt(totalSettlementPaid)} paid to the customer. The bill will carry each payment, its mode and its reference number.`}
              />
              <Popconfirm
                title="Generate bill for this case?"
                description="The bill will include the full settlement payment trail and close the case."
                onConfirm={() => doAction('Generate Bill', () => generateBill(id))}
              >
                <Button
                  type="primary"
                  size="large"
                  icon={<DollarCircleOutlined />}
                  loading={actionLoading === 'Generate Bill'}
                >
                  Generate Bill
                </Button>
              </Popconfirm>
            </Space>
          ) : (
            <Alert
              type="info"
              showIcon
              message="The bill is the final step."
              description={
                c.status === 'NEW'
                  ? 'Evaluate the gold items, pay the customer, and the bill can then be generated.'
                  : `Pay the customer in full first — ${fmt(Math.max(0, remainingSettlement))} still remaining on the Settlement step.`
              }
            />
          )}
        </div>
      ),
    },
  ];

  /* ════════════════════════════════════════
     RENDER
     ════════════════════════════════════════ */
  return (
    <>
      {contextHolder}
      {modalContextHolder}
      {deleteModalHolder}
      <div className="container-fluid" style={{ padding: 0 }}>

        {c.isDeleted && (
          <div style={{ padding: '16px 16px 0' }}>
            <Alert
              type="error"
              showIcon
              message="This case has been deleted"
              description={`Deleted${c.deletedBy?.name ? ` by ${c.deletedBy.name}` : ''}${c.deletedAt ? ` on ${dayjs(c.deletedAt).format('DD MMM YYYY, hh:mm A')}` : ''}. It is hidden from all lists.`}
              action={isAdmin && (
                <Space direction="vertical">
                  <Button size="small" onClick={() => confirmRestore({ what: `case ${c.caseId}`, onRestore: handleRestore })}>Restore</Button>
                  <Button size="small" danger onClick={() => confirmPermanentDelete({ what: `case ${c.caseId}`, onDelete: () => handleDelete(true) })}>
                    Delete permanently
                  </Button>
                </Space>
              )}
            />
          </div>
        )}

        {c.autoComplete?.status === 'FAILED' && (canGenerateBill || canMoveToVault) && (
          <div style={{ padding: '16px 16px 0' }}>
            <Alert
              type="warning"
              showIcon
              message={`Automatic ${c.autoComplete.step === 'VAULT' ? 'move to vault' : 'bill generation'} failed — please complete it manually`}
              description={`${c.autoComplete.error || 'Unknown error'}. Use the ${canGenerateBill ? 'Generate Bill' : 'Move to Vault'} button above.`}
            />
          </div>
        )}

        {/* ── Progress Stepper ── */}
        <div style={{ padding: '16px 16px 0' }}>
          <Card size="small" style={{ marginBottom: 8 }}>
            <Steps
              current={STATUS_STEP_INDEX[c.status] ?? 0}
              size="small"
              items={STEPS.map((s) => ({ title: s.title }))}
              responsive={false}
              style={{ overflowX: 'auto' }}
            />
          </Card>
        </div>

        {/* ── Header Card ── */}
        <div style={{ padding: '0 16px 0' }}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space size="middle" align="center">
                  <Title level={3} style={{ margin: 0 }}>{c.caseId}</Title>
                  <Tag color={STATUS_COLORS[c.status] || 'default'} style={{ fontSize: 12 }}>
                    {(c.status || '').replace(/_/g, ' ')}
                  </Tag>
                </Space>
              </Col>
              <Col>
                <Space wrap>
                  {/* Primary actions */}
                  {canGenerateBill && (
                    <Popconfirm
                      title="Generate bill for this case?"
                      description="The bill will include the full settlement payment trail and close the case."
                      onConfirm={() => { doAction('Generate Bill', () => generateBill(id)); setActiveTab('bill'); }}
                      okText="Generate Bill"
                      cancelText="Cancel"
                    >
                      <Button
                        size="small"
                        type="primary"
                        icon={<FileTextOutlined />}
                        loading={actionLoading === 'Generate Bill'}
                      >
                        Generate Bill
                      </Button>
                    </Popconfirm>
                  )}
                  {canMoveToVault && (
                    <Popconfirm
                      title="Move gold to branch vault?"
                      description="This will record the gold in the branch vault and generate a QR label."
                      onConfirm={handleMoveToVault}
                      okText="Move to Vault"
                      cancelText="Cancel"
                    >
                      <Button
                        size="small"
                        type="primary"
                        icon={<SafetyCertificateOutlined />}
                        loading={vaultLoading}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Move to Vault
                      </Button>
                    </Popconfirm>
                  )}
                  {c.inventoryStatus === 'IN_BRANCH_VAULT' && (
                    <Button
                      size="small"
                      icon={<SafetyCertificateOutlined />}
                      onClick={() => setVaultLabelVisible(true)}
                    >
                      Print Vault Label
                    </Button>
                  )}
                  <Button size="small" onClick={() => navigate('/walk-in-cases')}>Back</Button>
                  {canEditModule('walkInCase') && (
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => navigate(`/walk-in-cases/edit/${id}`)}
                      disabled={isCaseLocked}
                      title={isCaseLocked ? 'Case is settled — editing is disabled' : undefined}
                    >
                      {isCaseLocked && <LockOutlined style={{ marginRight: 4 }} />}Edit
                    </Button>
                  )}
                  {can('walkInCase', 'delete') && !c.isDeleted && (
                    <Button danger size="small" disabled={isCaseLocked && !isAdmin}
                      title={isCaseLocked && !isAdmin ? 'Case is settled — deletion is disabled' : undefined}
                      onClick={() => (isCaseLocked
                        // Money has moved: only an admin's permanent (cascade) delete applies.
                        ? confirmPermanentDelete({ what: `case ${c.caseId} and all its linked records`, onDelete: () => handleDelete(true) })
                        : confirmDelete({ what: `case ${c.caseId}`, onDelete: handleDelete }))}
                    >
                      Delete
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        </div>

        {/* ── Tabbed Content ── */}
        <div style={{ padding: '0 16px 16px' }}>
          <Card bodyStyle={{ padding: '12px 16px' }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              type="card"
              size="small"
              items={tabItems}
              destroyInactiveTabPane={false}
            />
          </Card>
        </div>
      </div>

      {/* ── Gold Vault Label ── */}
      <GoldVaultLabel
        caseData={wic}
        caseModel="WalkInPurchaseCase"
        visible={vaultLabelVisible}
        onClose={() => setVaultLabelVisible(false)}
      />

      {/* ── eSign Modal: completes on its own via webhook / signer return ── */}
      <Modal
        title="Aadhaar eSign"
        open={eSignModal.visible}
        onCancel={handleESignCancel}
        maskClosable={false}
        keyboard={false}
        footer={[
          <Button key="cancel" onClick={handleESignCancel}>Close</Button>,
          <Button key="finalize" loading={eSignLoading} onClick={handleESignFinalize}>
            Check status now
          </Button>,
        ]}
        width={520}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {wic?.esign?.status === 'failed' && wic?.esign?.documentId === eSignModal.documentId ? (
            <Alert
              type="error"
              showIcon
              message="Signing was not completed"
              description={wic.esign?.metadata?.webhook?.error || 'The signer rejected the document or the invitation expired. Close this and use Retry eSign.'}
            />
          ) : (
            <Space align="start">
              <Spin size="small" />
              <Text>
                Waiting for the customer to sign on Leegality. This closes by itself and saves the signed
                agreement the moment signing completes.
              </Text>
            </Space>
          )}
          {eSignWatcher.popupBlocked && (
            <Alert type="warning" showIcon message="The browser blocked the signing window — use “Open Signing Page”." />
          )}
          {eSignWatcher.popupClosed && wic?.esign?.status !== 'completed' && wic?.esign?.status !== 'failed' && (
            <Alert type="info" showIcon message="The signing window was closed. If the customer finished signing, this updates automatically; otherwise reopen the signing page." />
          )}
          <Space wrap>
            <Button type="primary" icon={<LinkOutlined />} onClick={() => eSignWatcher.open(eSignModal.signUrl)}>
              Open Signing Page
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(eSignModal.signUrl);
                messageApi.success('Signing link copied!');
              }}
            >
              Copy Signing Link
            </Button>
          </Space>
        </Space>
      </Modal>
    </>
  );
}
